import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Table,
  message,
  notification,
} from "antd";
import ButtonGroup from "antd/es/button/button-group";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiService, Control, Hazard } from "../../api";
import AddHazardModal from "./AddHazardModal";
import AddControlModal from "./AddControlModal";
import { NotificationType } from "../../constants";
import { apiErrorMessage } from "../../api/apiError";

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "select" | "text";
  record: Hazard;
  index: number;
  children: React.ReactNode;
  editable: boolean;
}

type Props = {
  schema: any;
  formProps: any;
  formId?: number;
  requestId?: number;
  readonly?: boolean;
  values?: any;
  onSuccess: (result: any) => void;
  duplicateRequestId?: number | undefined;
  setDuplicateRequestId?: (duplicateRequestId: number | undefined) => void;
  showPrev?: boolean;
  onPrev?: () => void;
  // Same contract as the dynamic form's (see FormViewPage): "Submit" saves the
  // values first, then marks the section done. Without it the page had to add a
  // Submit button of its own *outside* this component -- which sat on its own
  // row under Save, and marked the section done without saving what was typed.
  onSubmitSection?: () => Promise<void>;
  submitLabel?: string;
};

type ExtendedHazard = Hazard & {
  controls?: Control[];
};

const AssessmentFormComponent = ({
  formId,
  requestId,
  readonly,
  onSuccess,
  duplicateRequestId,
  setDuplicateRequestId,
  showPrev,
  onPrev,
  onSubmitSection,
  submitLabel,
}: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [hazards, setHazards] = useState<ExtendedHazard[]>([]);
  const [form] = Form.useForm();
  const [isAddHazardModalVisible, setIsAddHazardModalVisible] =
    useState<boolean>(false);
  const [isAddControlModalVisible, setIsAddControlModalVisible] =
    useState<boolean>(false);
  const [selectedHazard, setSelectedHazard] = useState<number | undefined>(
    undefined
  );

  const EditableCell: React.FC<EditableCellProps> = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {
    const inputNode =
      inputType === "select" ? (
        <Select disabled={readonly}>
          <Select.Option value="Low">{t("Low")}</Select.Option>
          <Select.Option value="Medium">{t("Medium")}</Select.Option>
          <Select.Option value="High">{t("High")}</Select.Option>
        </Select>
      ) : (
        <Input />
      );

    const id = record && record.id;

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={[id, dataIndex]}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: t("Please Input {title}!", { title }),
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const handleSetIsAddHazardModalVisible = (visible: boolean) => {
    setIsAddHazardModalVisible(visible);
  };

  const handleSetIsAddControlModalVisible = (visible: boolean) => {
    setIsAddControlModalVisible(visible);
  };

  useEffect(() => {
    (async () => {
      if (duplicateRequestId === undefined) {
        setLoading(true);

        getFormValues(requestId, formId);

        setLoading(false);
      }
    })();
  }, [form, formId, requestId]);

  useEffect(() => {
    (async () => {
      setLoading(true);

      getFormValues(duplicateRequestId, formId);

      setLoading(false);
    })();
  }, [duplicateRequestId]);

  const handleAddHazard = (hazard: Hazard) => {
    const isInArray = hazards.some((x) => x.id === hazard.id);
    if (isInArray) {
      message.error(t("This hazard already exists"));
    } else {
      setHazards((prev) => [...prev, hazard]);
    }
  };

  const openNotificationWithIcon = (
    type: NotificationType,
    description?: string
  ) => {
    if (type === "success") {
      notification[type]({
        message: t("Success"),
        description: t("Form has been successfully saved."),
      });
    } else {
      notification[type]({
        message: t("Error"),
        description: description || t("Something went wrong."),
      });
    }
  };

  const handleAddControl = (hazardId: number, control: Control) => {
    const hazard = hazards.find((x) => x.id === hazardId);

    const isInArray =
      hazard?.controls && hazard?.controls.some((x) => x.id === control.id);

    if (isInArray) {
      message.error(t("This control already exists"));
    } else {
      setHazards((prevHazards) =>
        prevHazards.map((h) =>
          h.id === hazardId
            ? { ...h, controls: [...(h.controls || []), control] }
            : h
        )
      );
    }
  };

  const getFormValues = async (requestId?: number, form_id?: number) => {
    if (!requestId) return undefined;
    const result = await ApiService.apiMainRequestFormRetrieve(
      form_id!,
      requestId!
    );

    const initialValues: Record<string, any> = {};

    setHazards(result);

    result.map((x: any) => {
      initialValues[x.id] = {
        initial_risk: x.initial_risk,
        residual_risk: x.residual_risk,
      };
    });

    form.setFieldsValue(initialValues);
  };

  const expandedRowRender = (record: ExtendedHazard) => {
    return (
      <Table
        dataSource={record.controls}
        bordered
        pagination={false}
        rowKey="id"
      >
        <Table.Column title={t("Name")} dataIndex="name"></Table.Column>
        <Table.Column
          title={t("Description")}
          dataIndex="description"
        ></Table.Column>
        <Table.Column
          width="15%"
          render={(control: Control) => (
            <Space size="middle">
              <Button
                type="link"
                onClick={() => handleDeleteControl(record.id, control.id)}
              >
                {t("Delete control")}
              </Button>
            </Space>
          )}
        ></Table.Column>
      </Table>
    );
  };

  /** Returns whether the values reached the server -- Submit must not mark a
      section done on top of a failed save. */
  const handleSave = async (values: any) => {
    setSaveLoading(true);
    const dataToSend = hazards.map((x) => {
      return {
        hazard_id: x.id,
        initial_risk: values[x.id].initial_risk,
        residual_risk: values[x.id].residual_risk,
        controls: x.controls?.map((x) => x.id) || [],
      };
    });

    let saved = false;
    try {
      const result = await ApiService.apiMainRequestFormCreate({
        form_id: formId ?? 0,
        request_id: requestId ?? null,
        data: dataToSend,
      });

      openNotificationWithIcon("success");
      onSuccess(result);
      getFormValues(requestId, formId);
      saved = true;
    } catch (e) {
      // The server's own words -- a stage that is not open, a section owned by
      // someone else. "Something went wrong." leaves the user nothing to act on.
      openNotificationWithIcon(
        "error",
        apiErrorMessage(e, t("The section could not be saved."))
      );
    }
    setSaveLoading(false);
    return saved;
  };

  // "Submit": save the typed-in risk levels first, then mark the section done.
  // `form.submit()` is fire-and-forget, so the two steps are sequenced by hand
  // here -- submitting straight from the table used to record the section as
  // finished while the levels the user had just chosen were never sent.
  const handleSaveAndSubmit = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch (e) {
      return; // antd has already flagged the offending cells
    }
    const saved = await handleSave(values);
    if (saved && onSubmitSection) await onSubmitSection();
  };

  const handleOpenControlModal = (hazardId: number) => {
    setSelectedHazard(hazardId);
    setIsAddControlModalVisible(true);
  };

  const handleDeleteHazard = (hazardId: number) => {
    setHazards((prevHazards) =>
      prevHazards.filter((hazard) => hazard.id !== hazardId)
    );
  };

  const handleDeleteControl = (hazardId: number, controlId: number) => {
    setHazards((prevHazards) =>
      prevHazards.map((hazard) =>
        hazard.id === hazardId
          ? {
              ...hazard,
              controls:
                hazard.controls?.filter(
                  (control) => control.id !== controlId
                ) || [],
            }
          : hazard
      )
    );
  };

  const columns = [
    {
      title: t("ID"),
      dataIndex: "id",
      editable: false,
    },
    {
      title: t("Name"),
      dataIndex: "name",
      editable: false,
    },
    {
      title: t("Description"),
      dataIndex: "description",
      editable: false,
    },

    {
      title: t("Initial Risk"),
      dataIndex: "initial_risk",
      editable: true,
    },
    {
      title: t("Residual Risk"),
      dataIndex: "residual_risk",
      editable: true,
    },
    {
      // One Space with a divider between the two links, not two Spaces around a
      // bare "|": each Space is its own inline-flex box, so the separator sat on
      // the baseline between two blocks that wrapped independently and the pair
      // broke across lines at the column's 15%. A fixed width sized to the two
      // translated labels keeps them on one line in every language.
      title: "",
      width: 260,
      render: (record: Hazard) => (
        <Space
          size={0}
          split={<Divider type="vertical" style={{ margin: 0 }} />}
          wrap={false}
        >
          <Button
            type="link"
            style={{ paddingInline: 8 }}
            onClick={() => handleOpenControlModal(record.id)}
          >
            {t("Add Control")}
          </Button>
          <Button
            type="link"
            danger
            style={{ paddingInline: 8 }}
            onClick={() => handleDeleteHazard(record.id)}
          >
            {t("Delete Hazard")}
          </Button>
        </Space>
      ),
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: Hazard) => ({
        record,
        inputType: col.dataIndex.includes("risk") ? "select" : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: col.editable,
      }),
    };
  });

  return (
    <div>
      <Form form={form} component={false} onFinish={handleSave}>
        <Table
          title={() => (
            <Button
              type="primary"
              onClick={() => setIsAddHazardModalVisible(true)}
            >
              {t("Add hazard")}
            </Button>
          )}
          bordered
          loading={loading}
          dataSource={hazards}
          expandable={{ expandedRowRender }}
          rowKey="id"
          columns={mergedColumns}
          rowClassName="editable-row"
          components={{
            body: {
              cell: EditableCell,
            },
          }}
        ></Table>
        {readonly !== true ? (
          <Row
            justify={showPrev ? "space-between" : "end"}
            align="middle"
            style={{ marginTop: "1em" }}
          >
            {showPrev && (
              <Col>
                <Button onClick={onPrev}>{t("Previous")}</Button>
              </Col>
            )}
            <Col>
              {/* Same pair, same order, same widths as every dynamic section --
                  see FormViewPage. */}
              <ButtonGroup>
                <Button
                  style={{ minWidth: 100 }}
                  type={onSubmitSection ? "default" : "primary"}
                  loading={saveLoading}
                  onClick={() => form.submit()}
                >
                  {t("Save")}
                </Button>
                {onSubmitSection && (
                  <Button
                    style={{ minWidth: 100 }}
                    type="primary"
                    loading={saveLoading}
                    onClick={handleSaveAndSubmit}
                  >
                    {submitLabel ?? t("Submit")}
                  </Button>
                )}
              </ButtonGroup>
            </Col>
          </Row>
        ) : null}
      </Form>
      <AddHazardModal
        visible={isAddHazardModalVisible}
        setVisible={handleSetIsAddHazardModalVisible}
        handleAddHazard={handleAddHazard}
      />
      <AddControlModal
        visible={isAddControlModalVisible}
        setVisible={handleSetIsAddControlModalVisible}
        handleAddControl={handleAddControl}
        hazardId={selectedHazard!}
      />
    </div>
  );
};

export default AssessmentFormComponent;
